import { Injectable } from '@angular/core';
import { Mouse, Keyboard, Gamepad, Touchscreen, StateEvent, TextEvent, TouchscreenState, MouseState } from "./inputdevice";
import { GamepadHandler } from "./gamepadhandler";
import { PointerCorrector } from "./pointercorrect";
import { LocalInputManager } from './local-input-manager.service';
import { GamepadButtonEvent, GamepadAxisEvent } from './gamepadEvents';

@Injectable({
  providedIn: 'root'
})
export class Sender extends LocalInputManager {
  private _devices: any[] = [];
  private _elem: HTMLVideoElement;
  private _corrector: PointerCorrector;
  private _gamepadHandler?: GamepadHandler;
  public mouse?: Mouse;
  public keyboard?: Keyboard;
  public gamepad?: Gamepad;
  public touchscreen?: Touchscreen;

  constructor(elem: HTMLVideoElement) {
    super();
    this._elem = elem;
    this._corrector = new PointerCorrector(
      this._elem.videoWidth,
      this._elem.videoHeight,
      this._elem
    );

    this._elem.addEventListener('resize', this._onResizeEvent.bind(this), false);
    const observer = new ResizeObserver(this._onResizeEvent.bind(this));
    observer.observe(this._elem);
  }

  addMouse(): void {
    const descriptionMouse = {
      m_InterfaceName: "RawInput",
      m_DeviceClass: "Mouse",
      m_Manufacturer: "",
      m_Product: "",
      m_Serial: "",
      m_Version: "",
      m_Capabilities: ""
    };
    this.mouse = new Mouse("Mouse", "Mouse", 1, null, descriptionMouse);
    this._devices.push(this.mouse);

    this._elem.addEventListener('click', this._onMouseEvent.bind(this), false);
    this._elem.addEventListener('mousedown', this._onMouseEvent.bind(this), false);
    this._elem.addEventListener('mouseup', this._onMouseEvent.bind(this), false);
    this._elem.addEventListener('mousemove', this._onMouseEvent.bind(this), false);
    this._elem.addEventListener('wheel', this._onWheelEvent.bind(this), false);
  }

  addKeyboard(): void {
    const descriptionKeyboard = {
      m_InterfaceName: "RawInput",
      m_DeviceClass: "Keyboard",
      m_Manufacturer: "",
      m_Product: "",
      m_Serial: "",
      m_Version: "",
      m_Capabilities: ""
    };
    this.keyboard = new Keyboard("Keyboard", "Keyboard", 2, null, descriptionKeyboard);
    this._devices.push(this.keyboard);

    document.addEventListener('keyup', this._onKeyEvent.bind(this), false);
    document.addEventListener('keydown', this._onKeyEvent.bind(this), false);
  }

  addGamepad(): void {
    const descriptionGamepad = {
      m_InterfaceName: "RawInput",
      m_DeviceClass: "Gamepad",
      m_Manufacturer: "",
      m_Product: "",
      m_Serial: "",
      m_Version: "",
      m_Capabilities: ""
    };
    this.gamepad = new Gamepad("Gamepad", "Gamepad", 3, null, descriptionGamepad);
    this._devices.push(this.gamepad);

    window.addEventListener("gamepadconnected", this._onGamepadEvent.bind(this), false);
    window.addEventListener("gamepaddisconnected", this._onGamepadEvent.bind(this), false);
    this._gamepadHandler = new GamepadHandler();
    this._gamepadHandler.addEventListener("gamepadupdated", this._onGamepadEvent.bind(this), false);
  }

  addTouchscreen(): void {
    const descriptionTouch = {
      m_InterfaceName: "RawInput",
      m_DeviceClass: "Touch",
      m_Manufacturer: "",
      m_Product: "",
      m_Serial: "",
      m_Version: "",
      m_Capabilities: ""
    };
    this.touchscreen = new Touchscreen("Touchscreen", "Touchscreen", 4, null, descriptionTouch);
    this._devices.push(this.touchscreen);

    this._elem.addEventListener('touchend', this._onTouchEvent.bind(this), false);
    this._elem.addEventListener('touchstart', this._onTouchEvent.bind(this), false);
    this._elem.addEventListener('touchcancel', this._onTouchEvent.bind(this), false);
    this._elem.addEventListener('touchmove', this._onTouchEvent.bind(this), false);
    this._elem.addEventListener('click', this._onTouchEvent.bind(this), false);
  }

  override get devices(): any[] {
    return this._devices;
  }

  private _onResizeEvent(): void {
    this._corrector.reset(
      this._elem.videoWidth,
      this._elem.videoHeight,
      this._elem
    );
  }

  public _onMouseEvent(event: MouseEvent): void {
    if (this.mouse) {
      this.mouse.queueEvent(event);
      if(this.mouse.currentState) {
        const currentState: MouseState = this.mouse.currentState as MouseState;
        currentState.position = this._corrector.map(currentState.position);
      }
      this._queueStateEvent(this.mouse.currentState, this.mouse);
    }
  }

  private _onWheelEvent(event: WheelEvent): void {
    if (this.mouse) {
      this.mouse.queueEvent(event);
      this._queueStateEvent(this.mouse.currentState, this.mouse);
    }
  }

  private _onKeyEvent(event: KeyboardEvent): void {
    if (this.keyboard) {
      if (event.type == 'keydown') {
        if (!event.repeat) { // StateEvent
          this.keyboard.queueEvent(event);
          this._queueStateEvent(this.keyboard.currentState, this.keyboard);
        }
        // TextEvent
        this._queueTextEvent(this.keyboard, event);
      } else if (event.type == 'keyup') {
        this.keyboard.queueEvent(event);
        this._queueStateEvent(this.keyboard.currentState, this.keyboard);
      }
    }
  }

  private _onTouchEvent(evt: Event): void {

    const event: TouchEvent = evt as TouchEvent;

    if (this.touchscreen) {
      this.touchscreen.queueEvent(event, this.timeSinceStartup);
      const currentState: TouchscreenState = this.touchscreen.currentState as TouchscreenState;
      for (let touch of currentState.touches) {
        let clone = touch.copy();
        clone.position = this._corrector.map(clone.position);
        this._queueStateEvent(clone, this.touchscreen);
      }
    }
  }

  // private _onTouchEvent(event: TouchEvent): void {
  //   if (this.touchscreen) {
  //     this.touchscreen.queueEvent(event, this.timeSinceStartup);
  //     for (let touch of this.touchscreen.currentState.touchData) {
  //       let clone = touch.copy();
  //       clone.position = this._corrector.map(clone.position);
  //       this._queueStateEvent(clone, this.touchscreen);
  //     }
  //   }
  // }

  // private _onGamepadEvent(event: GamepadEvent): void {
  //   if (!this.gamepad) return;

  //   switch (event.type) {
  //     case 'gamepadconnected': {
  //       this._gamepadHandler?.addGamepad(event.gamepad);
  //       break;
  //     }
  //     case 'gamepaddisconnected': {
  //       this._gamepadHandler?.removeGamepad(event.gamepad);
  //       break;
  //     }
  //     case 'gamepadupdated': {
  //       this.gamepad.queueEvent(event);
  //       this._queueStateEvent(this.gamepad.currentState, this.gamepad);
  //       break;
  //     }
  //   }
  // }

  private _onGamepadEvent(evt: Event): void {
    if (!this.gamepad) return;

    const event: GamepadEvent = evt as GamepadEvent;

    switch (event.type) {
      case 'gamepadconnected': {
        this._gamepadHandler?.addGamepad(event.gamepad);
        break;
      }
      case 'gamepaddisconnected': {
        this._gamepadHandler?.removeGamepad(event.gamepad);
        break;
      }
      case 'gamepadupdated': {
        if(evt instanceof GamepadButtonEvent || evt instanceof GamepadAxisEvent) {
          this.gamepad.queueEvent(evt);
        }
        this._queueStateEvent(this.gamepad.currentState, this.gamepad);
        break;
      }
    }
  }

  private _queueStateEvent(state: any, device: any): void {
    const stateEvent = StateEvent.fromState(state, device.deviceId, this.timeSinceStartup);
    const e = new CustomEvent('event', { detail: { event: stateEvent, device: device } });
    super.onEvent.emit(e);
  }

  private _queueTextEvent(device: any, event: any): void {
    const textEvent = TextEvent.create(device.deviceId, event, this.timeSinceStartup);
    const e = new CustomEvent('event', { detail: { event: textEvent, device: device } });
    super.onEvent.emit(e);
  }

  private _queueDeviceChange(device: any, usage: any): void {
    const e = new CustomEvent('changedeviceusage', { detail: { device: device, usage: usage } });
    super.onEvent.emit(e);
  }
}

export class Observer {
  private channel: RTCDataChannel;

  constructor(channel: RTCDataChannel) {
    this.channel = channel;
  }

  onNext(message: any): void {
    if (this.channel == null || this.channel.readyState != 'open') {
      return;
    }
    this.channel.send(message.buffer);
  }
}
